import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { ArrowLeft, Coins, Calendar, Clock, Wallet, Info } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const aed = (n) => `AED ${Number(n || 0).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => {
  if (!d) return '\u2014';
  try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

const PortalEOSB = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('portal_token');
  const [eosb, setEosb] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/employee-portal/my-eosb`, { headers: { Authorization: `Bearer ${token}` } });
        setEosb(r.data);
      } catch (e) {
        if (e?.response?.status === 401) navigate('/employee-portal/login');
      } finally { setLoading(false); }
    })();
  }, [token, navigate]);

  return (
    <div className="p-4 max-w-2xl mx-auto" data-testid="portal-eosb">
      <button onClick={() => navigate('/employee-portal')} className="flex items-center gap-1 text-sm text-violet-600 mb-4">
        <ArrowLeft className="h-4 w-4" /> Back to Home
      </button>

      <div className="flex items-center gap-3 mb-5">
        <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center">
          <Coins className="h-6 w-6 text-emerald-600" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900">EOSB / Gratuity</h1>
          <p className="text-xs text-gray-500">Your End of Service Benefits, estimated as of today</p>
        </div>
      </div>

      {loading ? (
        <Card><CardContent className="py-12 text-center text-gray-500">Loading…</CardContent></Card>
      ) : !eosb ? (
        <Card className="border-dashed"><CardContent className="py-16 text-center text-gray-500">EOSB details are not available.</CardContent></Card>
      ) : (
        <div className="space-y-4">
          {/* Gratuity headline */}
          <Card className="border-0 shadow-sm bg-gradient-to-br from-emerald-50 to-teal-50" data-testid="eosb-amount-card">
            <CardContent className="p-5">
              <p className="text-[11px] uppercase tracking-wider text-emerald-600 font-semibold">Estimated Gratuity</p>
              <p className="text-4xl font-bold text-emerald-700 mt-1" data-testid="eosb-amount">{aed(eosb.eosb_amount)}</p>
              <p className="text-xs text-emerald-600/80 mt-2">
                Based on {eosb.years_of_service} year{eosb.years_of_service === 1 ? '' : 's'} of service. This is an
                indicative estimate and the final settlement is confirmed by HR at the time of leaving.
              </p>
            </CardContent>
          </Card>

          {/* Key figures */}
          <div className="grid grid-cols-2 gap-3">
            <Card className="border-0 shadow-sm"><CardContent className="p-4">
              <Calendar className="h-5 w-5 text-sky-500 mb-1.5" />
              <p className="text-[10px] text-gray-400">Joining Date</p>
              <p className="text-sm font-semibold text-gray-800">{fmtDate(eosb.join_date)}</p>
            </CardContent></Card>
            <Card className="border-0 shadow-sm"><CardContent className="p-4">
              <Clock className="h-5 w-5 text-indigo-500 mb-1.5" />
              <p className="text-[10px] text-gray-400">Years of Service</p>
              <p className="text-sm font-semibold text-gray-800">{eosb.years_of_service}</p>
            </CardContent></Card>
            <Card className="border-0 shadow-sm"><CardContent className="p-4">
              <Wallet className="h-5 w-5 text-amber-500 mb-1.5" />
              <p className="text-[10px] text-gray-400">Basic Salary</p>
              <p className="text-sm font-semibold text-gray-800">{aed(eosb.basic_salary)}</p>
            </CardContent></Card>
            <Card className="border-0 shadow-sm"><CardContent className="p-4">
              <Wallet className="h-5 w-5 text-teal-500 mb-1.5" />
              <p className="text-[10px] text-gray-400">Total Salary</p>
              <p className="text-sm font-semibold text-gray-800">{aed(eosb.total_salary)}</p>
            </CardContent></Card>
          </div>

          {eosb.lop_days > 0 && (
            <div className="flex items-center gap-2 py-2 px-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
              <Info className="h-4 w-4 flex-shrink-0" />
              {eosb.lop_days} loss-of-pay day(s) are deducted from the service period used above.
            </div>
          )}

          {/* Calculation breakdown */}
          {eosb.calculation_details && (
            <Card className="border-0 shadow-sm" data-testid="eosb-details-card">
              <CardContent className="p-4">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">How this is calculated</p>
                <pre className="text-[11px] leading-relaxed text-gray-600 whitespace-pre-wrap font-mono">{eosb.calculation_details}</pre>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default PortalEOSB;
